import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeading } from '@/components/shared/PageHeading/PageHeading';
import { Button } from '@/components/ui/Button/Button';
import { Illustration } from '@/components/ui/Illustration/Illustration';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import { toast } from '@/components/ui/Toast/Toast';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import type { NotificationItem } from '@/types/notification';
import { cn } from '@/utils';
import { useQueryClient } from '@tanstack/react-query';
import { GET_NOTIFICATIONS_INFINITE_QUERY_KEY, useGetNotificationsInfinite } from '../../api/getNotifications';
import { useMarkAllNotificationsAsRead } from '../../api/markAllNotificationsAsRead';
import { NotificationListItem } from '../NotificationListItem/NotificationListItem';

type TAB = 'all' | 'manual' | 'automatic';
const DEFAULT_TAB: TAB = 'all';

interface NotificationsListProps {
  isStandalonePage?: boolean;
  closePopover?: () => void;
}

export const NotificationsList = ({ isStandalonePage = false, closePopover }: NotificationsListProps) => {
  const { t } = useTranslation('notifications', { keyPrefix: 'notificationsList' });
  const { translateError } = useErrorTranslation();

  const queryClient = useQueryClient();

  const [currentTab, setCurrentTab] = useState<TAB>(DEFAULT_TAB);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetNotificationsInfinite({
    limit: 20,
    categoryType: currentTab === 'all' ? undefined : currentTab,
  });

  const { loadMoreRef } = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage: () => void fetchNextPage(),
  });

  const notifications: NotificationItem[] = useMemo(() => {
    return data?.pages.flatMap((page) => page.notifications) ?? [];
  }, [data]);

  const { mutate, isPending } = useMarkAllNotificationsAsRead({
    categoryType: currentTab === 'all' ? undefined : currentTab,
    options: {
      onSuccess: () => {
        void queryClient.refetchQueries({ queryKey: [GET_NOTIFICATIONS_INFINITE_QUERY_KEY] });
        void captureAnalyticsEvent('notifications_markallread');
      },
      onError: (error) => {
        toast({
          variant: 'destructive',
          description: translateError(error),
        });
      },
    },
  });

  const HeadingElement = isStandalonePage ? 'h1' : 'h2';

  return (
    <div className={cn('flex flex-col gap-3', isStandalonePage && 'h-full flex-1')}>
      <div
        className={cn(
          'bg-fill-background-neutral desktop:sticky desktop:gap-3 fixed top-[var(--system-banner-height,0px)] left-0 z-50 flex flex-col',
          !isStandalonePage && 'desktop:top-0',
        )}
      >
        <PageHeading
          withBackButton={isStandalonePage}
          isSubpage
          as='div'
          containerClassName={cn('border-none desktop:pt-3 relative top-0', !isStandalonePage && 'px-6 ')}
          className='w-full'
        >
          <div className={cn('flex w-full flex-1 items-center justify-between gap-4', !isStandalonePage && 'h-11')}>
            <HeadingElement className={cn(!isStandalonePage && 'text-heading-s-medium')} id='notifications-title'>
              {t('title')}
            </HeadingElement>
            {!isLoading && !error && notifications.length > 0 && (
              <Button
                variant='link'
                className='text-text-default-secondary self-end pr-1'
                isLoading={isPending}
                onClick={() => {
                  mutate();
                }}
                aria-busy={isPending}
                disabled={isPending}
              >
                {t('markAllReadButton.label')}
              </Button>
            )}
          </div>
        </PageHeading>
        <Tabs
          defaultValue={DEFAULT_TAB}
          value={currentTab}
          className='desktop:w-full -mt-6 w-screen'
          onValueChange={(value) => {
            void captureAnalyticsEvent('notifications_filtered', { filter: value });
            setCurrentTab(value as TAB);
          }}
        >
          <TabsList className={cn('desktop:px-0 w-full justify-start gap-1 px-5', !isStandalonePage && 'desktop:px-4')}>
            <TabsTrigger className='px-2' value='all'>
              {t('tabs.all')}
            </TabsTrigger>
            <TabsTrigger className='px-2' value='automatic'>
              {t('tabs.automatic')}
            </TabsTrigger>
            <TabsTrigger className='px-2' value='manual'>
              {t('tabs.manual')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className='flex flex-1 flex-col'>
        {isLoading && (
          <div className={cn('flex flex-1 items-center justify-center', !isStandalonePage && 'mt-[220px]')}>
            <Spinner className='size-16 stroke-1' />
          </div>
        )}
        {!isLoading && error && (
          <div className={cn('flex flex-1 flex-col items-center justify-center gap-6', !isStandalonePage && 'mt-[140px]')}>
            <Illustration name='problem' className='size-[130px]' />
            <p className='text-heading-m-semibold'>{t('loadingError')}</p>
            <p className='text-text-default-secondary -mt-3'>{translateError(error)}</p>
          </div>
        )}
        {!isLoading && !error && notifications.length === 0 && (
          <div className={cn('flex flex-1 flex-col items-center justify-center gap-6', !isStandalonePage && 'mt-[160px]')}>
            <Illustration name='megaphone' className='size-[130px]' />
            <p className='text-text-default-secondary'>{t('emptyState')}</p>
          </div>
        )}
        {!isLoading && !error && notifications.length > 0 && (
          <ul
            className={cn('desktop:mt-0 mt-8 flex flex-col gap-4 px-4', {
              'tablet:py-5 tablet:px-10 px-0': isStandalonePage,
            })}
          >
            {notifications.map((notification: NotificationItem) => (
              <NotificationListItem key={notification.id} notification={notification} closePopover={closePopover} />
            ))}

            <div ref={loadMoreRef} className='h-1' />

            {isFetchingNextPage && (
              <div className='flex justify-center py-4'>
                <Spinner className='size-8 stroke-1' />
              </div>
            )}

            {!hasNextPage && !isFetchingNextPage && <span className='text-text-default-secondary py-[120px] text-center'>{t('noMore')}</span>}
          </ul>
        )}
      </div>
    </div>
  );
};
